import React, { useEffect, useRef } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import './CelEditor.css';

export interface CelVocab {
  variables: { name: string; type: string; detail: string }[];
  functions: { name: string; signature: string; detail: string }[];
  roles: string[];
  tools: string[];
}

interface CelEditorProps {
  value: string;
  onChange: (v: string) => void;
  vocab: CelVocab;
  placeholder?: string;
}

const KEYWORDS = ['in', 'true', 'false', 'null'];

/** Builds a context-aware completion source from the policy vocabulary. */
function makeCompletions(vocab: CelVocab) {
  return (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos);
    const before = line.text.slice(0, context.pos - line.from);

    // Inside a string literal? Suggest concrete values (tool names, roles) — no quotes.
    const insideString = (before.match(/"/g) || []).length % 2 === 1;
    if (insideString) {
      const word = context.matchBefore(/[\w.-]*$/);
      const from = word ? word.from : context.pos;
      const options = [
        ...vocab.tools.map((t) => ({ label: t, type: 'text', detail: 'tool' })),
        ...vocab.roles.map((r) => ({ label: r, type: 'text', detail: 'role' })),
      ];
      if (!options.length) return null;
      return { from, options, validFor: /[\w.-]*$/ };
    }

    // Identifier position: variables, functions, keywords.
    const word = context.matchBefore(/\w*$/);
    if (!word || (word.from === word.to && !context.explicit)) return null;

    const options = [
      ...vocab.variables.map((v) => ({
        label: v.name,
        type: 'variable',
        detail: v.type,
        info: v.detail,
      })),
      ...vocab.functions.map((f) => ({
        label: f.name,
        type: 'function',
        // Show only the argument list as detail so the popup doesn't read
        // "startsWithstartsWith(s, prefix)".
        detail: f.signature.startsWith(f.name) ? f.signature.slice(f.name.length) : f.signature,
        info: f.detail,
        // Insert `name()` and drop the cursor between the parens.
        apply: (view: EditorView, _c: unknown, from: number, to: number) => {
          view.dispatch({
            changes: { from, to, insert: `${f.name}()` },
            selection: { anchor: from + f.name.length + 1 },
          });
        },
      })),
      ...KEYWORDS.map((k) => ({ label: k, type: 'keyword' })),
    ];
    return { from: word.from, options, validFor: /^\w*$/ };
  };
}

const baseTheme = EditorView.theme({
  '&': {
    fontSize: '15px',
    backgroundColor: 'var(--surface)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
  },
  '&.cm-focused': { outline: 'none', borderColor: 'var(--accent)' },
  '.cm-content': {
    fontFamily: 'var(--mono)',
    fontSize: '15px',
    lineHeight: '1.6',
    padding: '12px 14px',
    caretColor: 'var(--text)',
    minHeight: '92px',
  },
  '.cm-cursor': { borderLeftColor: 'var(--text)' },
  '.cm-tooltip': {
    backgroundColor: 'var(--bg)',
    border: '1px solid var(--border)',
    color: 'var(--text)',
    borderRadius: 'var(--radius-sm)',
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]': {
    backgroundColor: 'var(--accent)',
    color: '#fff',
  },
  '.cm-completionDetail': { color: 'var(--text-3)', fontStyle: 'normal' },
});

const CelEditor: React.FC<CelEditorProps> = ({ value, onChange, vocab, placeholder }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Keep the completion source in a compartment so it can be reconfigured when
  // the vocabulary (e.g. live tool names) arrives without rebuilding the editor.
  const completionCompartment = useRef(new Compartment());

  // Mount once.
  useEffect(() => {
    if (!hostRef.current || viewRef.current) return;
    const state = EditorState.create({
      doc: value,
      extensions: [
        history(),
        closeBrackets(),
        keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap, ...completionKeymap]),
        completionCompartment.current.of(
          autocompletion({ override: [makeCompletions(vocab)], activateOnTyping: true })
        ),
        cmPlaceholder(placeholder || 'e.g. role in ["developer","admin"] && startsWith(tool, "database-tools.")'),
        EditorView.lineWrapping,
        baseTheme,
        EditorView.updateListener.of((u) => {
          if (u.docChanged) onChangeRef.current(u.state.doc.toString());
        }),
      ],
    });
    viewRef.current = new EditorView({ state, parent: hostRef.current });
    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconfigure completions when vocab changes.
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: completionCompartment.current.reconfigure(
        autocompletion({ override: [makeCompletions(vocab)], activateOnTyping: true })
      ),
    });
  }, [vocab]);

  // Sync external value changes (e.g. form reset) without clobbering typing.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (value !== current) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
    }
  }, [value]);

  return <div className="cel-editor" ref={hostRef} />;
};

export default CelEditor;
